/**
 * blocks.jsx — pinta los bloques de un tema.
 *
 * El registro sólo declara datos; aquí se decide cómo se ven. Los tipos de
 * bloque están cerrados y documentados en docs/ACADEMIA_CMDB.md:
 *   text · steps · shot · note · sandbox · live
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { SANDBOXES } from './sandboxes';
import { resolveShot } from './shots';

/** Negrita (**texto**) y código (`texto`), nada más: no es un Markdown completo. */
export function InlineText({ children }) {
  if (!children) return null;
  const parts = String(children).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={index} className="ac-code">{part.slice(1, -1)}</code>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
}

function TextBlock({ body }) {
  return <p className="ac-text"><InlineText>{body}</InlineText></p>;
}

function StepsBlock({ items }) {
  return (
    <ol className="ac-steps">
      {items.map((item, index) => (
        <li key={index}><InlineText>{item}</InlineText></li>
      ))}
    </ol>
  );
}

function NoteBlock({ tone, body }) {
  const isWarn = tone === 'warn';
  return (
    <div className={`ac-note ac-note--${isWarn ? 'warn' : 'tip'}`}>
      {isWarn ? <ExclamationTriangleIcon className="ac-note-icon" /> : <LightBulbIcon className="ac-note-icon" />}
      <p><InlineText>{body}</InlineText></p>
    </div>
  );
}

/**
 * Captura de la app real. Si el archivo todavía no existe se ve un marco con
 * la ruta esperada: la Academia nunca miente diciendo que hay una imagen que
 * no hay.
 */
function ShotBlock({ src, caption, alt }) {
  return (
    <figure className="ac-shot">
      <img
        className="ac-shot-img"
        src={resolveShot(src)}
        alt={alt || caption || ''}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
          const fallback = event.currentTarget.nextElementSibling;
          if (fallback) fallback.hidden = false;
        }}
      />
      <div className="ac-shot-missing" hidden>
        <span>Captura pendiente</span>
        <code>{src}</code>
      </div>
      {caption && <figcaption className="ac-shot-caption"><InlineText>{caption}</InlineText></figcaption>}
    </figure>
  );
}

function SandboxBlock({ demo }) {
  const Component = SANDBOXES[demo];
  if (!Component) {
    return <div className="ac-note ac-note--warn">Sandbox «{demo}» no implementada.</div>;
  }
  return (
    <div className="ac-sandbox">
      <div className="ac-sandbox-head">
        <span className="ac-sandbox-chip">Sandbox</span>
        <span className="ac-sandbox-hint">De mentira: puedes probarlo sin tocar tus datos.</span>
      </div>
      <Component />
    </div>
  );
}

function LiveBlock({ label, to }) {
  return (
    <Link className="ac-live" to={to}>
      <span>{label}</span>
      <ArrowTopRightOnSquareIcon className="ac-live-icon" />
    </Link>
  );
}

export default function BlockRenderer({ blocks }) {
  return (
    <div className="ac-blocks">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'text':
            return <TextBlock key={index} {...block} />;
          case 'steps':
            return <StepsBlock key={index} {...block} />;
          case 'note':
            return <NoteBlock key={index} {...block} />;
          case 'shot':
            return <ShotBlock key={index} {...block} />;
          case 'sandbox':
            return <SandboxBlock key={index} {...block} />;
          case 'live':
            return <LiveBlock key={index} {...block} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
